import { Dialog } from '@progress/kendo-react-dialogs';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { AccessConfiguration } from 'app/components/access-sharing';
import { CurrentUserAccessCard } from 'app/components/access-sharing/cards';
import { SharedUserAccess } from 'app/components/access-sharing/cards/SharedUserAccessCard/types';
import { SubmitButton } from 'app/components/form-fields';
import { BackActionDialogContentLayout } from 'app/components/layouts';
import useClickOutside from 'app/hooks/useClickOutside';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IAccessSharingPeriodType,
  IAccessSharingPermissionType,
} from 'types/IAccessSharingUser';
import './ChangeUserFleetPermissionsDialog.less';
import { useNotificationsContext } from 'context';
import {
  SharedItem,
  SharedItemShareType,
  sharesApi,
} from 'services/api/shares';

interface Props {
  fleet: SharedItem;
  userAccess: SharedUserAccess;
  onClose: () => void;
}

export default function ChangeUserFleetPermissionsDialog({
  fleet,
  userAccess,
  onClose,
}: Props) {
  const { t } = useTranslation();
  // TODO needs to be replaces with useTryMutate when API is ready
  const { addNotification } = useNotificationsContext();
  const [updateUserPermissions] = sharesApi.useUpdateUserPermissionsMutation();
  const [isLoading, setIsLoading] = useState(false);

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(onClose);

  const [permission, setPermission] = useState(
    IAccessSharingPermissionType.Viewer,
  );
  const [period, setPeriod] = useState(IAccessSharingPeriodType.Continuous);

  // TODO for testing purposes. Need to be refactored when API is ready
  const initialStartDate = userAccess.startTime
    ? new Date(userAccess.startTime)
    : null;
  const initialEndDate = userAccess.endTime
    ? new Date(userAccess.endTime)
    : null;

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const { firstName, lastName, email, role } = userAccess;

  async function handleSave() {
    try {
      setIsLoading(true);
      await updateUserPermissions({
        shareType: SharedItemShareType.FLEET,
        exposedId: [fleet.sharedItemDetails.exposedId],
        sharedWith: [
          {
            email,
            role: permission,
            startsAt: startDate ? startDate.toISOString() : null,
            endsAt: endDate ? endDate.toISOString() : null,
          },
        ],
      }).unwrap();

      addNotification(
        <Success
          text={t('access_sharing.snackbars.user_permissions_updated')}
        />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={t('server_error_message')} />);
    } finally {
      setIsLoading(false);
      onClose();
    }
  }

  return (
    <Dialog className="modal-container">
      <BackActionDialogContentLayout
        className="change-user-fleet-permissions-dialog"
        onBack={onClose}
      >
        <h2 className="change-user-fleet-permissions-dialog__title">
          {t('access_sharing.machine_details.change_users_permissions')}
        </h2>

        <div className="change-user-fleet-permissions-dialog__model">
          <span className="change-user-fleet-permissions-dialog__model-label">
            {t('label_name')}
          </span>
          {fleet.sharedItemDetails.sharedItemLabel}
        </div>

        <CurrentUserAccessCard
          name={`${firstName} ${lastName}`}
          email={email}
          role={role}
          className="change-user-fleet-permissions-dialog__current-user-access-card"
        />

        <AccessConfiguration
          initialPermission={permission}
          period={period}
          startDate={startDate}
          endDate={endDate}
          onChangePermission={setPermission}
          onChangePeriod={setPeriod}
          onChangeStartDate={setStartDate}
          onChangeEndDate={setEndDate}
        />

        <SubmitButton onClick={handleSave} disabled={isLoading}>
          {t('save')}
        </SubmitButton>
      </BackActionDialogContentLayout>
    </Dialog>
  );
}
